import React, {
  DetailedHTMLProps,
  MetaHTMLAttributes,
  useContext,
  useEffect,
} from 'react';
import { Script } from 'gatsby';

import { CartContext } from '@repo/contexts/CartContext';
import { DealerSearchContext } from '@repo/contexts/DealerSearchContext';
import { LocaleContext } from '@repo/contexts/LocaleContext';
import { SiteSettingsContext } from '@repo/contexts/SiteSettingsContext';

import { InitInsights } from '@repo/utils/algoliaAnalytics';

import DealerGateModal from '@repo/ui-components/dealer-search/DealerGateModal';
import CountryPickerModal from '@repo/ui-components/notification-banner/CountryPickerModal';
import Button from '@repo/ui-components/button/Button';
import Cart from '@repo/ui-components/cart/Cart';
import DealerSearchFlyout from '@repo/ui-components/dealer-search/DealerSearchFlyout';
import Footer from '@repo/ui-components/footer/Footer';
import SkipToContent from '@repo/ui-components/skip-to-content';
import Head from '@repo/ui-components/Head';
import {
  MavenoidChatButton,
  MavenoidChatFooter,
} from '@repo/ui-components/openChatWidget';
import NotificationBanner from '@repo/ui-components/notification-banner/NotificationBanner';
import PageWrapper from '@repo/ui-components/page-wrapper/PageWrapper';
import Toast from '@repo/ui-components/cart/Toast';
import UnsupportedBrowserMessage from '@repo/ui-components/UnsupportedBrowserMessage';
import { Header, MinimalHeader } from '@repo/ui-components/header/Header';
import { Toaster } from '@repo/ui-components/shadcn/Sonner';

import getInternalLink from '@components/utils/getInternalLink';
import styles from '@components/utils/styles';

import '../styles/globals.css';
import 'react-image-gallery/styles/css/image-gallery.css';
import 'lite-youtube-embed/src/lite-yt-embed.css';

function ChatButton() {
  return <MavenoidChatButton buttonText="Start a Chat" />;
}

function FooterChatButton() {
  return <MavenoidChatFooter footerText="Start a Chat" />;
}

interface LayoutProps {
  children: React.ReactNode;
  headTagData?: {
    title: string;
    description?: string;
    keywords?: string;
    meta?: DetailedHTMLProps<
      MetaHTMLAttributes<HTMLMetaElement>,
      HTMLMetaElement
    >[];
    productSchema?: string;
    includeBVScript?: boolean;
    canonicalUrl?: string;
    noIndex?: boolean;
    noFollow?: boolean;
  };
  scriptsData?: JSX.Element[];
  fullWidth?: boolean;
  minimalHeader?: boolean;
  minimalFooter?: boolean;
  location?: {
    href: string;
    pathname: string;
    search: string;
  };
  isDealerPage?: boolean;
  includeBVScript?: boolean;
}

interface DealerSearchContextProps {
  dealerGateModalActive: boolean;
  dealerSearchActive: boolean;
  storedDealerPanelVisible: boolean;
}

function Layout({
  children = null,
  headTagData = {
    title: '',
    description: '',
    keywords: '',
    meta: [],
    productSchema: '',
    includeBVScript: false,
    canonicalUrl: '',
    noIndex: false,
    noFollow: false,
  },
  scriptsData = [],
  fullWidth = false,
  minimalHeader = false,
  minimalFooter = false,
  location = {
    href: '',
    pathname: '',
    search: '',
  },
  isDealerPage = false,
  includeBVScript = false,
}: Readonly<LayoutProps>) {
  const { cartError } = useContext(CartContext);
  const { currency, countryPickerActive } = useContext(LocaleContext);
  const { siteSettings } = useContext(SiteSettingsContext);
  const {
    dealerGateModalActive,
    dealerSearchActive,
    storedDealerPanelVisible,
  } = useContext(DealerSearchContext) as DealerSearchContextProps;

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);

    // lowercase urlParam keys
    urlParams.forEach((value, key) => {
      urlParams.set(key.toLowerCase(), value);
    });

    const marketingParams = [
      'client_id',
      'hubspot_token',
      'promo',
      'promo_code',
      'promoCode',
      'referral_code',
      'utm_campaign',
      'utm_content',
      'utm_medium',
      'utm_source',
    ];

    marketingParams.forEach(param => {
      const value = urlParams.get(param);
      // Upsert values into session storage
      if (value) {
        sessionStorage.setItem(param, value);
      }
    });
  }, []);

  InitInsights();

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const script = document.createElement('script');
    script.id = 'optanon-wrapper';
    script.innerHTML = `
    function OptanonWrapper() {
      var analyticsConsent = window.OptanonActiveGroups?.includes('C0002');
      var adsConsent = window.OptanonActiveGroups?.includes('C0004');
      window.gtag && window.gtag('consent', 'update', {
        analytics_storage: analyticsConsent ? 'granted' : 'denied',
        ad_storage: adsConsent ? 'granted' : 'denied',
        ad_user_data: adsConsent ? 'granted' : 'denied',
        ad_personalization: adsConsent ? 'granted' : 'denied'
      });
    }
  `;
    document.head.appendChild(script);
  }, []);

  const siteUrl =
    process.env.CONTEXT === 'production' ||
    process.env.CONTEXT === 'branch-deploy'
      ? process.env.URL
      : process.env.DEPLOY_PRIME_URL;

  useEffect(() => {
    if (location?.pathname?.includes('boundary-plus-wired')) {
      window.mavenoid.push({
        event: 'assistant-unmount',
      });
    } else {
      window.mavenoid.push({
        event: 'assistant-mount',
        clientId: 'ma_6pcjkgimki_aq6hhsv0va7bb5nlcau9umk8u8kd3h9i88ie',
      });
    }
  }, [location.pathname]);

  // Handle Mavenoid Screen Reader visibility when modals are active
  useEffect(() => {
    const mavenoidAssistant = document.querySelector('mavenoid-assistant');

    if (dealerSearchActive || storedDealerPanelVisible) {
      mavenoidAssistant?.setAttribute('aria-hidden', 'true');
    } else {
      mavenoidAssistant?.removeAttribute('aria-hidden');
    }
  }, [dealerSearchActive, storedDealerPanelVisible]);

  const hideFromScreenReader = dealerSearchActive || storedDealerPanelVisible;

  return (
    <>
      {currency && <CountryPickerModal active={countryPickerActive} />}
      <DealerGateModal active={dealerGateModalActive} closeButtonEnabled />
      <Head
        title={headTagData.title}
        description={headTagData.description}
        keywords={headTagData.keywords}
        meta={headTagData.meta}
        schemaOrgObj={headTagData.productSchema}
        scriptTags={scriptsData}
        noIndex={headTagData.noIndex}
        noFollow={headTagData.noFollow}
      >
        <link key="typekit" rel="dns-prefetch" href="https://use.typekit.net" />
        <link
          key="onetrust-preconnect"
          rel="preconnect"
          href="https://geolocation.onetrust.com"
        />
        <link
          key="gtm-preconnect"
          rel="preconnect"
          href="https://www.googletagmanager.com"
        />
        <link
          key="cookielaw-preconnect"
          rel="preconnect"
          href="https://cdn.cookielaw.org"
        />
        <link
          key="vwo-preconnect"
          rel="preconnect"
          href="https://dev.visualwebsiteoptimizer.com"
        />
        {location?.pathname && !isDealerPage && (
          <link
            key="x-default-hreflang"
            rel="alternate"
            href={
              location.pathname.includes('/en-ca')
                ? `${siteUrl}${location.pathname.replace('/en-ca', '')}`
                : `${siteUrl}${location.pathname}`
            }
          />
        )}
        {location?.pathname && !isDealerPage && (
          <link
            key="en-us-hreflang"
            rel="alternate"
            href={
              location.pathname.includes('/en-ca')
                ? `${siteUrl}${location.pathname.replace('/en-ca', '')}`
                : `${siteUrl}${location.pathname}`
            }
            hrefLang="en-US"
          />
        )}
        {location?.pathname && !isDealerPage && (
          <link
            key="en-ca-hreflang"
            rel="alternate"
            href={
              location.pathname.includes('/en-ca')
                ? `${siteUrl}${location.pathname}`
                : `${siteUrl}/en-ca${location.pathname}`
            }
            hrefLang="en-CA"
          />
        )}
        {headTagData.canonicalUrl && (
          <link
            key="canonical"
            rel="canonical"
            href={headTagData.canonicalUrl}
          />
        )}
        <script key="vice-code" type="text/javascript">
          {`vice= {
              config: {
                viceAccountId: 'petsafe',
                viceSiteId: 'invisiblefence',
                viceSectionId: ''
              }
            }
          `}
        </script>
        <script
          key="vice-prod.sdiapi.com"
          src="https://vice-prod.sdiapi.com/vice_loader/petsafe/invisiblefence"
        />
        <script type="text/javascript" id="vwoCode" key="vwo-code">
          {`window._vwo_code || (function() {
          var account_id=${process.env.GATSBY_VWO_ACCOUNT_ID},
          version=2.1,
          settings_tolerance=2000,
          hide_element='body',
          hide_element_style = 'opacity:0 !important;filter:alpha(opacity=0) !important;background:none !important',
          /* DO NOT EDIT BELOW THIS LINE */
          f=false,w=window,d=document,v=d.querySelector('#vwoCode'),cK='_vwo_'+account_id+'_settings',cc={};try{var c=JSON.parse(localStorage.getItem('_vwo_'+account_id+'_config'));cc=c&&typeof c==='object'?c:{}}catch(e){}var stT=cc.stT==='session'?w.sessionStorage:w.localStorage;code={use_existing_jquery:function(){return typeof use_existing_jquery!=='undefined'?use_existing_jquery:undefined},library_tolerance:function(){return typeof library_tolerance!=='undefined'?library_tolerance:undefined},settings_tolerance:function(){return cc.sT||settings_tolerance},hide_element_style:function(){return'{'+(cc.hES||hide_element_style)+'}'},hide_element:function(){if(performance.getEntriesByName('first-contentful-paint')[0]){return''}return typeof cc.hE==='string'?cc.hE:hide_element},getVersion:function(){return version},finish:function(e){if(!f){f=true;var t=d.getElementById('_vis_opt_path_hides');if(t)t.parentNode.removeChild(t);if(e)(new Image).src='https://dev.visualwebsiteoptimizer.com/ee.gif?a='+account_id+e}},finished:function(){return f},addScript:function(e){var t=d.createElement('script');t.type='text/javascript';if(e.src){t.src=e.src}else{t.text=e.text}d.getElementsByTagName('head')[0].appendChild(t)},load:function(e,t){var i=this.getSettings(),n=d.createElement('script'),r=this;t=t||{};if(i){n.textContent=i;d.getElementsByTagName('head')[0].appendChild(n);if(!w.VWO||VWO.caE){stT.removeItem(cK);r.load(e)}}else{var o=new XMLHttpRequest;o.open('GET',e,true);o.withCredentials=!t.dSC;o.responseType=t.responseType||'text';o.onload=function(){if(t.onloadCb){return t.onloadCb(o,e)}if(o.status===200){_vwo_code.addScript({text:o.responseText})}else{_vwo_code.finish('&e=loading_failure:'+e)}};o.onerror=function(){if(t.onerrorCb){return t.onerrorCb(e)}_vwo_code.finish('&e=loading_failure:'+e)};o.send()}},getSettings:function(){try{var e=stT.getItem(cK);if(!e){return}e=JSON.parse(e);if(Date.now()>e.e){stT.removeItem(cK);return}return e.s}catch(e){return}},init:function(){if(d.URL.indexOf('__vwo_disable__')>-1)return;var e=this.settings_tolerance();w._vwo_settings_timer=setTimeout(function(){_vwo_code.finish();stT.removeItem(cK)},e);var t;if(this.hide_element()!=='body'){t=d.createElement('style');var i=this.hide_element(),n=i?i+this.hide_element_style():'',r=d.getElementsByTagName('head')[0];t.setAttribute('id','_vis_opt_path_hides');v&&t.setAttribute('nonce',v.nonce);t.setAttribute('type','text/css');if(t.styleSheet)t.styleSheet.cssText=n;else t.appendChild(d.createTextNode(n));r.appendChild(t)}else{t=d.getElementsByTagName('head')[0];var n=d.createElement('div');n.style.cssText='z-index: 2147483647 !important;position: fixed !important;left: 0 !important;top: 0 !important;width: 100% !important;height: 100% !important;background: white !important;';n.setAttribute('id','_vis_opt_path_hides');n.classList.add('_vis_hide_layer');t.parentNode.insertBefore(n,t.nextSibling)}var o='https://dev.visualwebsiteoptimizer.com/j.php?a='+account_id+'&u='+encodeURIComponent(d.URL)+'&vn='+version;if(w.location.search.indexOf('_vwo_xhr')!==-1){this.addScript({src:o})}else{this.load(o+'&x=true')}}};w._vwo_code=code;code.init();})();`}
        </script>
      </Head>
      <SkipToContent />
      <Cart
        highlightColor={styles.colors.blue[500]}
        headerBackgroundColor={styles.colors.gray[600]}
        textColor={styles.colors.gray[500]}
        checkoutButtonComponent={Button}
      />
      <DealerSearchFlyout />
      <Toast
        headerBackgroundColor={styles.colors.gray[600]}
        headerTextColor={styles.colors.white}
        textColor={styles.colors.gray[500]}
      />
      {cartError && (
        <div className="flex items-center justify-center text-center bg-muted-foreground p-2 text-base text-primary-foreground">
          Purchases cannot be completed in the store right now. Please refresh
          or try again later.
        </div>
      )}

      {minimalHeader || (
        <>
          <NotificationBanner
            messageText={siteSettings?.notificationBanner?.text}
            messageLink={siteSettings?.notificationBanner?.link?.href}
            messageInternalLink={
              siteSettings?.notificationBanner?.link?.internalLink
            }
            messageType={siteSettings?.notificationBanner?.link?.linkType}
            messageSvg={siteSettings?.notificationBanner?.blockSvg}
            getInternalLink={getInternalLink}
            ChatComponent={ChatButton}
            sticky
            hasDealerSearch
            hideFromScreenReader={dealerSearchActive}
          />
          <Header
            menuStructure={siteSettings?.mainMenu}
            searchIndexes={['products', 'blogs', 'pages']}
            getInternalLink={getInternalLink}
            hasDealerSearch
            searchHitsPerPage={3}
            currency={currency}
            brandName="Invisible Fence Brand"
            hideFromScreenReader={hideFromScreenReader}
          />
        </>
      )}

      {minimalHeader && (
        <MinimalHeader
          currency={currency}
          brandName="Invisible Fence Brand"
          hideFromScreenReader={hideFromScreenReader}
        />
      )}

      <UnsupportedBrowserMessage />
      <PageWrapper
        fullWidth={fullWidth}
        background="inherit"
        padding="0"
        aria-hidden={hideFromScreenReader}
      >
        <main id="content">{children}</main>
      </PageWrapper>

      <div aria-hidden={hideFromScreenReader}>
        <Toaster theme="light" richColors closeButton />
      </div>

      <Footer
        ChatComponent={FooterChatButton}
        columns={siteSettings?.footerCustomColumns}
        footerCopyright={siteSettings?.footerCopyright}
        footerLegal={siteSettings?.footerLegal}
        footerSocialIcons={siteSettings?.socialMedia}
        getInternalLink={getInternalLink}
        minimalFooter={minimalFooter}
        hideFromScreenReader={hideFromScreenReader}
      />
      <Script
        id="gtag-consent-defaults"
        dangerouslySetInnerHTML={{
          __html: `
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('consent', 'default', {
            ad_storage: 'granted',
            ad_user_data: 'granted',
            ad_personalization: 'granted',
            analytics_storage: 'granted',
            wait_for_update: 500
          });
        `,
        }}
      />
      <Script
        key="otSDKStub"
        src="https://cdn.cookielaw.org/scripttemplates/otSDKStub.js"
        data-domain-script={process.env.GATSBY_COOKIEPRO_SITE_ID}
      />
      <Script
        src={`https://js.hs-scripts.com/${process.env.GATSBY_HUBSPOT_PORTAL_ID}.js`}
        id="hs-script-loader"
        strategy="post-hydrate"
      />
      <Script id="mavenoid-embed" strategy="post-hydrate">
        {`
          (function(m,a,v,e,n,o,i,d) {
            n=m.createElement(a);
            n.async=true;
            n.src="https://radiosystemscorp.mavenoid.com/embedded/embedded.js";
            o=m.getElementsByTagName(a)[0];
            o.parentNode.insertBefore(n,o);
            v[e]=v[e]||[];
          })(document,"script",window,"mavenoid");

          // Load mavenoid chat icon
          window.mavenoid.push({
            event: 'assistant-mount',
            clientId: 'ma_6pcjkgimki_aq6hhsv0va7bb5nlcau9umk8u8kd3h9i88ie',
          });
        `}
      </Script>
      {includeBVScript && (
        <Script
          defer
          key="bazaarvoice"
          src={`https://apps.bazaarvoice.com/deployments/invisiblefence/main_site/${process.env.GATSBY_BAZAARVOICE_ENVIRONMENT}/en_US/bv.js`}
        />
      )}
    </>
  );
}

export default Layout;
